import { useEffect, useState } from "react";

import { logger } from "src/core/globals";
import { DeviceModel } from "src/gen/schema/models/bom";
import { ConfigD } from "src/utils/api/configD";
import {
  isBindableDeviceModel,
  isVisibleDeviceModel
} from "src/utils/deviceModelOrderability";

type Registry<T> = Record<string, T>;

export interface DeviceModelHelper {
  /**
   * Get device model by model name
   * @param model
   */
  getDeviceByModelName: (model: string | undefined) => DeviceModel | undefined;
  /**
   * Get all device models
   */
  getDeviceModels: () => DeviceModel[];
  /**
   * Get all device model names
   */
  getDeviceModelNames: () => string[];
  /**
   * Check if a device model is bindable (DEVICE_ORDERABLE_TYPE_BINDABLE)
   */
  isBindableDeviceModelName: (model: string | undefined) => boolean;
  /**
   * Check if a device model should be visible in the UI.
   * Hides DEVICE_ORDERABLE_TYPE_UNSPECIFIED unless developer setting is on.
   */
  isVisibleDeviceModelName: (model: string | undefined) => boolean;
}

const deviceModelFallbackHelpers: DeviceModelHelper = {
  getDeviceByModelName: () => undefined,
  getDeviceModels: () => [],
  getDeviceModelNames: () => [],
  isBindableDeviceModelName: () => true,
  isVisibleDeviceModelName: () => true
};
export const useDeviceModels = (): DeviceModelHelper => {
  // State to hold the mapping from model name to DeviceModel object
  const [helpers, setDeviceModels] = useState<DeviceModelHelper>({
    getDeviceByModelName: () => undefined,
    getDeviceModels: () => [],
    getDeviceModelNames: () => [],
    isBindableDeviceModelName: () => false,
    isVisibleDeviceModelName: () => false
  });

  useEffect(() => {
    const tid = setTimeout(() => {
      ConfigD.getDeviceModels({})
        .then((resp: DeviceModel[]) => {
          // Create a new Map to store modelName -> DeviceModel mapping
          const deviceModels: Registry<DeviceModel> = {};
          // Iterate over each device model returned by the API
          resp.forEach((d) => {
            // Use modelName as the key and the DeviceModel object as the value
            deviceModels[d.modelName] = d;
          });
          // Update state with the new Map, triggering a re-render
          setDeviceModels({
            getDeviceByModelName: (model: string = "") => deviceModels[model],
            getDeviceModels: () => Object.values(deviceModels),
            getDeviceModelNames: () => Object.keys(deviceModels),
            isBindableDeviceModelName: (model: string = "") =>
              isBindableDeviceModel(deviceModels[model]),
            isVisibleDeviceModelName: (model: string = "") =>
              isVisibleDeviceModel(deviceModels[model])
          });
        })
        .catch((err) => {
          logger.error("Failed to fetch device models", err);
          setDeviceModels(deviceModelFallbackHelpers);
        });
    }, 0);
    return () => clearTimeout(tid);
  }, []);

  // Return the helpers for device models.
  return helpers;
};
